/**
 * Componente de configuração para condição de Webhook Externo
 * 
 * Exibe URL do webhook, permite configurar mapeamento de campos
 * e oferece opção de regenerar token.
 * 
 * @version 1.0.0
 * @date 2026-01-30
 */

import { useState } from 'react';
import { Copy, RefreshCw, Check, ExternalLink, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { getEdgeFunctionUrl } from '@/lib/supabaseHelpers';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

// Logger para depuração
const logger = createLogger('WebhookConditionConfig');

// ============= INTERFACES =============

interface FieldMapping {
  contact_phone: string;
  contact_name: string;
}

interface WebhookConditionConfigProps {
  /** Token do webhook (gerado automaticamente ao criar) */
  webhookToken?: string | null;
  /** ID da regra (necessário para regenerar token) */
  ruleId?: string;
  /** Mapeamento de campos do payload */
  fieldMapping: FieldMapping;
  /** Callback quando mapeamento muda */
  onFieldMappingChange: (mapping: FieldMapping) => void;
  /** Callback quando token é regenerado */
  onTokenRegenerated?: (newToken: string) => void;
  /** Se está em modo de criação (não mostra URL ainda) */
  isCreating?: boolean;
}

// ============= COMPONENTE PRINCIPAL =============

export function WebhookConditionConfig({
  webhookToken,
  ruleId,
  fieldMapping,
  onFieldMappingChange,
  onTokenRegenerated,
  isCreating = false,
}: WebhookConditionConfigProps) {
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  // Construir URL do webhook usando helper centralizado
  // Encode do token para preservar caracteres especiais (+, =, etc.)
  const webhookUrl = webhookToken 
    ? `${getEdgeFunctionUrl('routing-webhook')}?token=${encodeURIComponent(webhookToken)}`
    : '';

  // Copiar URL para clipboard
  const copyToClipboard = async () => {
    if (!webhookUrl) return;
    
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setCopied(true);
      toast.success('URL copiada para a área de transferência');
      
      // Resetar estado após 2 segundos
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logger.error('Erro ao copiar URL', err);
      toast.error('Erro ao copiar URL');
    }
  };

  // Regenerar token
  const handleRegenerateToken = async () => {
    if (!ruleId) {
      toast.error('ID da regra não disponível');
      return;
    }

    setRegenerating(true);
    
    try {
      const { data, error } = await (supabase.rpc as any)('regenerate_routing_webhook_token', {
        p_rule_id: ruleId,
      });

      if (error) throw error;

      toast.success('Token regenerado com sucesso');
      
      if (onTokenRegenerated && data) {
        onTokenRegenerated(data);
      }
    } catch (err) {
      logger.error('Erro ao regenerar token', err);
      toast.error('Erro ao regenerar token');
    } finally {
      setRegenerating(false);
    }
  };

  // Atualizar mapeamento de campo
  const updateFieldMapping = (field: keyof FieldMapping, value: string) => {
    onFieldMappingChange({
      ...fieldMapping,
      [field]: value,
    });
  };

  return (
    <div className="space-y-4">
      {/* Alerta explicativo para modo criação */}
      {isCreating && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Webhook Externo</AlertTitle>
          <AlertDescription>
            Após criar a regra, você receberá uma URL única que pode ser configurada em sistemas externos 
            (ClickSign, ERPs, CRMs, etc.) para disparar automaticamente as ações configuradas abaixo.
          </AlertDescription>
        </Alert>
      )}

      {/* URL do Webhook (apenas se já existe token) */}
      {!isCreating && webhookToken && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <ExternalLink className="h-4 w-4" />
              URL do Webhook
            </CardTitle>
            <CardDescription>
              Configure esta URL no sistema externo para receber os gatilhos.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* URL com botão copiar */}
            <div className="flex gap-2">
              <Input 
                value={webhookUrl} 
                readOnly 
                className="font-mono text-xs bg-muted/50"
              />
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={copyToClipboard}
                    className="shrink-0"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {copied ? 'Copiado!' : 'Copiar URL'}
                </TooltipContent>
              </Tooltip>
            </div>

            {/* Método HTTP */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Badge variant="outline" className="font-mono">POST</Badge>
              <span>Envie um JSON com os dados do contato</span>
            </div>

            {/* Botão regenerar token */}
            {ruleId && (
              <div className="pt-2 border-t">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRegenerateToken}
                  disabled={regenerating}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${regenerating ? 'animate-spin' : ''}`} />
                  {regenerating ? 'Regenerando...' : 'Regenerar Token'}
                </Button>
                <p className="text-xs text-muted-foreground mt-1">
                  Atenção: Isso invalidará a URL atual e criará uma nova.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Mapeamento de Campos */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Mapeamento de Campos</CardTitle>
          <CardDescription>
            Indique o caminho dos campos no JSON que será recebido pelo webhook.
            Use dot notation para campos aninhados (ex: <code className="bg-muted px-1 rounded">signatario.telefone</code>).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Campo telefone (obrigatório) */}
          <div className="space-y-2">
            <Label htmlFor="contact_phone" className="flex items-center gap-1">
              Telefone do Contato
              <Badge variant="secondary" className="text-xs ml-1">obrigatório</Badge>
            </Label>
            <Input
              id="contact_phone"
              value={fieldMapping.contact_phone}
              onChange={(e) => updateFieldMapping('contact_phone', e.target.value)}
              placeholder="Ex: telefone, signatario.telefone, data.phone"
            />
            <p className="text-xs text-muted-foreground">
              Caminho para o campo de telefone no payload JSON
            </p>
          </div>

          {/* Campo nome (opcional) */}
          <div className="space-y-2">
            <Label htmlFor="contact_name" className="flex items-center gap-1">
              Nome do Contato
              <Badge variant="outline" className="text-xs ml-1">opcional</Badge>
            </Label>
            <Input
              id="contact_name"
              value={fieldMapping.contact_name}
              onChange={(e) => updateFieldMapping('contact_name', e.target.value)}
              placeholder="Ex: nome, signatario.nome, data.name"
            />
            <p className="text-xs text-muted-foreground">
              Caminho para o campo de nome no payload JSON
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Exemplo de Payload */}
      <Card className="bg-muted/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Exemplo de Payload</CardTitle>
        </CardHeader>
        <CardContent>
          <pre className="text-xs bg-background rounded p-3 overflow-x-auto">
{`// Exemplo de payload JSON esperado:
{
  "${fieldMapping.contact_phone.split('.')[0]}": ${fieldMapping.contact_phone.includes('.') ? `{
    "${fieldMapping.contact_phone.split('.').slice(1).join('.')}": "5511999999999"
  }` : '"5511999999999"'},
  "${fieldMapping.contact_name.split('.')[0]}": ${fieldMapping.contact_name.includes('.') ? `{
    "${fieldMapping.contact_name.split('.').slice(1).join('.')}": "João Silva"
  }` : '"João Silva"'}
}`}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}
